"use client"

import React, { useState, useEffect } from "react"
import { X, Plus, Loader2, FileText, Search, Filter, ChevronDown, Edit, Trash2, Save } from "lucide-react";
import { 
  getIPRRequirements, 
  createIPRRequirements, 
  updateIPRRequirement, 
  deleteIPRRequirement,
  validateIPRRequirementData
} from "../../../../ApiCalls/mantisFlowsRequeriments"
import { Button } from "../../../components/ui/button"
import { useToast } from "../../../components/ui/use-toast"

export default function IPRRequirementsModal({ clientId, onClose, embedded = false }) {
  const [requirements, setRequirements] = useState([])
  const [filteredRequirements, setFilteredRequirements] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingRequirement, setEditingRequirement] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [stats, setStats] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterMandatory, setFilterMandatory] = useState("all")
  const [showFilter, setShowFilter] = useState(false)

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    mandatory: false
  })

  const { toast } = useToast()
  const selectedClientId = clientId

  useEffect(() => {
    if (clientId) {
      loadRequirements()
      loadStats()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId])

  useEffect(() => {
    const filterRequirements = () => {
      let filtered = [...requirements]

      if (searchTerm) {
        filtered = filtered.filter(req =>
          req.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (req.description && req.description.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      }

      if (filterMandatory === "mandatory") {
        filtered = filtered.filter(req => req.mandatory === true)
      } else if (filterMandatory === "optional") {
        filtered = filtered.filter(req => req.mandatory === false)
      }

      setFilteredRequirements(filtered)
    }

    filterRequirements()
  }, [requirements, searchTerm, filterMandatory])

  const loadRequirements = async () => {
    try {
      setLoading(true)
      console.log(`Loading requirements for clientId: ${selectedClientId}`)
      
      const response = await getIPRRequirements(selectedClientId)
      console.log("API Response:", response)
      
      // Manejar diferentes estructuras de respuesta
      let requirementsData = []
      if (response && response.data) {
        requirementsData = response.data
      } else if (response && Array.isArray(response)) {
        requirementsData = response
      } else if (response && response.count !== undefined) {
        requirementsData = response.data || []
      }
      
      console.log("Requirements data:", requirementsData)
      setRequirements(requirementsData)
    } catch (error) {
      console.error("Error loading requirements:", error)
      toast({
        title: "Error",
        description: "No se pudieron cargar los requirements",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const response = await getIPRRequirements(selectedClientId)
      
      // Manejar diferentes estructuras de respuesta
      let requirementsData = []
      if (response && response.data) {
        requirementsData = response.data
      } else if (response && Array.isArray(response)) {
        requirementsData = response
      } else if (response && response.count !== undefined) {
        requirementsData = response.data || []
      }
      
      const stats = {
        total: requirementsData.length,
        mandatory: requirementsData.filter(req => req.mandatory).length,
        optional: requirementsData.filter(req => !req.mandatory).length
      }
      
      setStats(stats)
    } catch (error) {
      console.error("Error loading stats:", error)
    }
  }

  const handleCreateRequirement = () => {
    setFormData({
      name: "",
      description: "",
      mandatory: false
    })
    setEditingRequirement(null)
    setShowForm(true)
  }

  const handleEditRequirement = (requirement) => {
    setFormData({
      name: requirement.name || "",
      description: requirement.description || "",
      mandatory: requirement.mandatory || false
    })
    // Guardamos el requirement completo para tener acceso al ID
    setEditingRequirement(requirement)
    setShowForm(true)
  }

  const handleSaveRequirement = async () => {
    try {
      const validation = validateIPRRequirementData(formData)
      if (!validation.isValid) {
        toast({
          title: "Error de validación",
          description: validation.errors.join(", "),
          variant: "destructive"
        })
        return
      }

      setSaving(true)
      
      if (editingRequirement) {
        // Usar el ID UUID del requirement que estamos editando
        const requirementId = editingRequirement.id
        console.log("Updating requirement with ID:", requirementId)
        const result = await updateIPRRequirement(selectedClientId, requirementId, formData)
        console.log("Update result:", result)
        toast({
          title: "Éxito",
          description: "Requirement actualizado exitosamente"
        })
      } else {
        const result = await createIPRRequirements(selectedClientId, formData)
        console.log("Create result:", result)
        toast({
          title: "Éxito", 
          description: "Requirement creado exitosamente"
        })
      }
      
      setShowForm(false)
      await loadRequirements()
      await loadStats()
    } catch (error) {
      console.error("Error saving requirement:", error)
      toast({
        title: "Error",
        description: "Error al guardar el requirement",
        variant: "destructive"
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteRequirement = async () => {
    if (!deleteConfirm) return

    setSaving(true)
    try {
      const requirementToDelete = requirements.find(req => 
        req.id === deleteConfirm || req.name === deleteConfirm
      )
      
      if (!requirementToDelete) {
        throw new Error("Requirement no encontrado")
      }
      
      // Usar el ID UUID del requirement
      const requirementId = requirementToDelete.id
      console.log("Deleting requirement with ID:", requirementId)
      await deleteIPRRequirement(selectedClientId, requirementId)
      
      toast({
        title: "Requirement eliminado",
        description: `"${requirementToDelete.name}" se ha eliminado correctamente`,
        variant: "default"
      })
      
      await loadRequirements()
      await loadStats()
      setDeleteConfirm(null)
    } catch (error) {
      console.error("Error eliminando requirement:", error)
      toast({
        title: "Error",
        description: "No se pudo eliminar el requirement",
        variant: "destructive"
      })
    } finally {
      setSaving(false)
    }
  }

  if (!embedded) {
    return (
      <>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded shadow-2xl w-[1200px] h-[800px] flex flex-col overflow-hidden">
            
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-slate-800 dark:to-slate-700">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-emerald-500 rounded flex items-center justify-center mr-3">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                    Purchase Requirements
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Gestiona los requirements obligatorios y opcionales para las solicitudes de compra
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {stats && (
                  <div className="flex items-center gap-3 mr-4 text-xs">
                    <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded font-medium">
                      Total: {stats.total}
                    </span>
                    <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded font-medium">
                      Obligatorios: {stats.mandatory}
                    </span>
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium">
                      Opcionales: {stats.optional}
                    </span>
                  </div>
                )}
                <Button
                  onClick={handleCreateRequirement}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  disabled={loading}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Requirement
                </Button>
                <Button variant="ghost" size="icon" onClick={onClose}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Search and Filter Bar */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar requirements por nombre o descripción..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-slate-800 dark:text-slate-200"
                  />
                </div>
                <Button
                  onClick={() => setShowFilter(!showFilter)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Filter className="h-4 w-4" />
                  Filtros
                  <ChevronDown className={`h-4 w-4 transition-transform ${showFilter ? 'rotate-180' : ''}`} />
                </Button>
              </div>

              {showFilter && (
                <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Estado:
                    </label>
                    <select
                      value={filterMandatory}
                      onChange={(e) => setFilterMandatory(e.target.value)}
                      className="px-3 py-1 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-emerald-500 dark:bg-slate-700 dark:text-slate-200 text-sm"
                    >
                      <option value="all">Todos</option>
                      <option value="mandatory">Solo Obligatorios</option>
                      <option value="optional">Solo Opcionales</option>
                    </select>
                    <Button
                      onClick={() => {
                        setSearchTerm("")
                        setFilterMandatory("all")
                      }}
                      variant="ghost"
                      size="sm"
                      className="text-sm"
                    >
                      Limpiar Filtros
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {showForm ? (
                <div className="h-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-800/50">
                  <div className="max-w-2xl mx-auto">
                    <div className="bg-white dark:bg-slate-900 rounded shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                          {editingRequirement ? "Editar Requirement" : "Crear Nuevo Requirement"}
                        </h3>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowForm(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Nombre del Requirement <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-slate-800 dark:text-slate-200"
                            placeholder="Ej: Factura Proforma, Cotización, etc."
                            maxLength="255"
                          />
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Máximo 255 caracteres
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Descripción
                          </label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-slate-800 dark:text-slate-200 min-h-[100px]"
                            placeholder="Describe el propósito y uso de este requirement..."
                            maxLength="1000"
                          />
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Máximo 1000 caracteres
                          </p>
                        </div>

                        <div>
                          <label className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={formData.mandatory}
                              onChange={(e) => setFormData(prev => ({ ...prev, mandatory: e.target.checked }))}
                              className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                            />
                            <div>
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                Requirement Obligatorio
                              </span>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                Si está marcado, este requirement será obligatorio para todas las solicitudes de compra
                              </p>
                            </div>
                          </label>
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-slate-200 dark:border-slate-700">
                          <Button
                            variant="outline"
                            onClick={() => setShowForm(false)}
                            disabled={saving}
                          >
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleSaveRequirement}
                            disabled={saving || !formData.name.trim()}
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            {saving ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            Guardar Requirement
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-8 w-8 text-emerald-500 animate-spin mb-4" />
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Cargando...</p>
                  </div>
                </div>
              ) : filteredRequirements.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <FileText className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200 mb-2">
                      {searchTerm || filterMandatory !== "all" ? "No se encontraron requirements" : "No hay Requirements"}
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400 mb-4">
                      {searchTerm || filterMandatory !== "all" ? 
                        "Intenta ajustar tus filtros de búsqueda" : 
                        "Crea tu primer requirement para comenzar a gestionar los documentos requeridos"
                      }
                    </p>
                    {!searchTerm && filterMandatory === "all" && (
                      <Button onClick={handleCreateRequirement} className="bg-emerald-500 hover:bg-emerald-600">
                        <Plus className="h-4 w-4 mr-2" />
                        Crear Primer Requirement
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-6 h-full overflow-y-auto">
                  <div className="grid gap-4">
                    {filteredRequirements.map((requirement, index) => (
                      <div
                        key={requirement.id || requirement.name || index}
                        className="bg-white dark:bg-slate-900 rounded shadow-sm border border-slate-200 dark:border-slate-700 p-6 hover:shadow-md hover:border-emerald-200 dark:hover:border-emerald-700 transition-all duration-200"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h5 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                {requirement.name}
                              </h5>
                              {requirement.mandatory ? (
                                <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-medium">
                                  Obligatorio
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                                  Opcional
                                </span>
                              )}
                            </div>
                            
                            {requirement.description && (
                              <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
                                {requirement.description.length > 100 
                                  ? `${requirement.description.substring(0, 100)}...` 
                                  : requirement.description
                                }
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              onClick={() => handleEditRequirement(requirement)}
                              variant="ghost"
                              size="sm"
                              className="h-9 w-9 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 text-slate-500 dark:text-slate-400 hover:scale-105 transition-all duration-200"
                              title="Editar requirement"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => setDeleteConfirm(requirement.id)}
                              variant="ghost"
                              size="sm"
                              className="h-9 w-9 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 text-slate-500 dark:text-slate-400 hover:scale-105 transition-all duration-200"
                              title="Eliminar requirement"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal de confirmación de eliminación */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded shadow-xl w-[400px] border border-slate-200 dark:border-slate-700">
              <div className="p-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                    Confirmar Eliminación
                  </h4>
                </div>
                
                <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
                  ¿Estás seguro que quieres eliminar este requirement?
                </p>
                
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteConfirm(null)}
                    disabled={saving}
                    size="sm"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleDeleteRequirement}
                    disabled={saving}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    size="sm"
                  >
                    {saving ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3 mr-1" />
                    )}
                    Eliminar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // Versión Embedded
  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-900">
      {/* Header Embedded */}
      <div className="flex items-center justify-between p-6 border-b border-slate-200/60 dark:border-slate-700/60 bg-gradient-to-r from-slate-50/50 to-emerald-50/30 dark:from-slate-900/50 dark:to-emerald-900/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-100 dark:shadow-emerald-900/20">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
              Purchase Requirements
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Gestiona documentos requeridos
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {stats && (
            <div className="flex items-center gap-2">
              <div className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {stats.total} requirements
                  </span>
                </div>
              </div>
            </div>
          )}
          <Button
            onClick={handleCreateRequirement}
            size="sm"
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-100 dark:shadow-emerald-900/20 border-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo
          </Button>
        </div>
      </div>

      {/* Content Embedded */}
      <div className="flex-1 overflow-auto p-4">
        {showForm ? (
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200/60 dark:border-slate-700/60 overflow-hidden">
            {/* Header del formulario */}
            <div className="flex items-center justify-between p-6 bg-gradient-to-r from-emerald-50/50 to-teal-50/30 dark:from-emerald-900/10 dark:to-teal-900/10 border-b border-slate-200/60 dark:border-slate-700/60">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded flex items-center justify-center">
                  {editingRequirement ? (
                    <Edit className="h-4 w-4 text-white" />
                  ) : (
                    <Plus className="h-4 w-4 text-white" />
                  )}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                    {editingRequirement ? "Editar Requirement" : "Nuevo Requirement"}
                  </h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {editingRequirement ? "Modifica la información del requirement" : "Crea un nuevo documento requerido"}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowForm(false)}
                className="hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Contenido del formulario */}
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Nombre del Requirement <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-slate-800 dark:text-slate-200 transition-all duration-200 placeholder:text-slate-400"
                  placeholder="Ej: Factura Proforma, Orden de Compra..."
                  maxLength="255"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 ml-1">
                  Máximo 255 caracteres
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-slate-800 dark:text-slate-200 min-h-[100px] transition-all duration-200 placeholder:text-slate-400 resize-none"
                  placeholder="Describe el propósito y cuándo es necesario este documento..."
                  maxLength="1000"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 ml-1">
                  Máximo 1000 caracteres
                </p>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.mandatory}
                      onChange={(e) => setFormData(prev => ({ ...prev, mandatory: e.target.checked }))}
                      className="w-5 h-5 text-emerald-600 border-slate-300 dark:border-slate-600 rounded focus:ring-emerald-500 focus:ring-2 transition-all duration-200"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 block mb-1">
                      Requirement Obligatorio
                    </span>
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                      Si está marcado, este requirement será obligatorio para todas las solicitudes de compra. Los usuarios no podrán proceder sin proporcionar esta información.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Footer del formulario */}
            <div className="flex justify-end gap-3 p-6 bg-slate-50/50 dark:bg-slate-800/30 border-t border-slate-200/60 dark:border-slate-700/60">
              <Button
                variant="outline"
                onClick={() => setShowForm(false)}
                disabled={saving}
                className="px-6 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveRequirement}
                disabled={saving || !formData.name.trim()}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-100 dark:shadow-emerald-900/20 border-0 px-6"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editingRequirement ? "Actualizar" : "Crear"} Requirement
              </Button>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="flex flex-col items-center">
              <Loader2 className="h-6 w-6 text-emerald-500 animate-spin mb-2" />
              <p className="text-slate-600 dark:text-slate-400 text-sm">Cargando...</p>
            </div>
          </div>
        ) : filteredRequirements.length === 0 ? (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <div className="text-center max-w-md mx-auto">
              <div className="relative mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                  <FileText className="h-10 w-10 text-slate-400 dark:text-slate-500" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
                  <Plus className="h-3 w-3 text-white" />
                </div>
              </div>
              
              <h4 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3 tracking-tight">
                No hay Requirements
              </h4>
              <p className="text-slate-500 dark:text-slate-400 mb-8 text-sm leading-relaxed">
                Comienza creando tu primer requirement para gestionar<br />
                los documentos necesarios en tus procesos de compra
              </p>
              
              <Button 
                onClick={handleCreateRequirement} 
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-100 dark:shadow-emerald-900/20 border-0 px-6 py-2.5" 
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear Primer Requirement
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequirements.map((requirement, index) => (
              <div
                key={requirement.id || requirement.name || index}
                className="group relative bg-white dark:bg-slate-900 rounded-xl border border-slate-200/60 dark:border-slate-700/60 p-6 hover:border-emerald-200 dark:hover:border-emerald-700/50 hover:shadow-lg hover:shadow-emerald-100/20 dark:hover:shadow-emerald-900/10 transition-all duration-300 ease-out"
              >
                {/* Header con nombre y badge */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 tracking-tight">
                        {requirement.name}
                      </h3>
                      {requirement.mandatory ? (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border border-red-200/50 dark:border-red-700/50 text-red-700 dark:text-red-300 rounded-full text-xs font-medium">
                          <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                          Obligatorio
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200/50 dark:border-blue-700/50 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                          Opcional
                        </div>
                      )}
                    </div>
                    
                    {requirement.description && (
                      <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                        {requirement.description.length > 120 
                          ? `${requirement.description.substring(0, 120)}...` 
                          : requirement.description
                        }
                      </p>
                    )}
                  </div>

                  {/* Botones de acción */}
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      onClick={() => handleEditRequirement(requirement)}
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 text-slate-500 dark:text-slate-400 hover:scale-105 transition-all duration-200"
                      title="Editar requirement"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => setDeleteConfirm(requirement.id)}
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 text-slate-500 dark:text-slate-400 hover:scale-105 transition-all duration-200"
                      title="Eliminar requirement"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Footer con metadata si existe */}
                {requirement.created_at && (
                  <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                      <div className="w-1 h-1 bg-slate-400 rounded-full"></div>
                      Creado el {new Date(requirement.created_at).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                )}

                {/* Indicador visual lateral */}
                <div className={`absolute left-0 top-6 bottom-6 w-1 rounded-r-full transition-all duration-300 ${
                  requirement.mandatory 
                    ? 'bg-gradient-to-b from-red-400 to-rose-500' 
                    : 'bg-gradient-to-b from-blue-400 to-cyan-500'
                } opacity-0 group-hover:opacity-100`}></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de confirmación de eliminación para embedded */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded shadow-xl w-[350px] border border-slate-200 dark:border-slate-700">
            <div className="p-4">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
                  <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
                <h4 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Confirmar Eliminación
                </h4>
              </div>
              
              <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
                ¿Estás seguro que quieres eliminar este requirement?
              </p>
              
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={saving}
                  size="sm"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDeleteRequirement}
                  disabled={saving}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  size="sm"
                >
                  {saving ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3 mr-1" />
                  )}
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 